import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ApiResponse } from "../../services/base/apiResponse";
import { StudentInfoAdminResponse } from "../../models/dtos";
import { studentDocumentsApi } from "../../services/studentDocuments/studentDocumentsApi";
import { Button } from "primereact/button";
import StudentCertificateDialog from "./StudentCertificateDialog";
import { useState } from "react";
import { Tag } from "primereact/tag";
import FilePreviewDialog from "../../components/common/FilePreviewDialog";
import TranscriptOfRecordsDialog from "./TranscriptOfRecordsDialog";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { format } from "date-fns";

interface StudentDocumentsProps {
  student?: ApiResponse<StudentInfoAdminResponse>;
}

const StudentDocuments = ({ student }: StudentDocumentsProps) => {
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfTitle, setPdfTitle] = useState("Уверение");
  const [showTranscriptDialog, setShowTranscriptDialog] = useState(false);

  const [generateCertificate] = studentDocumentsApi.useGenerateStudentStatusCertificateMutation();
  const [updateCertificate] = studentDocumentsApi.useUpdateStudentStatusCertificateMutation();
  const [finalizeCertificate] = studentDocumentsApi.useFinalizeStudentDocumentMutation();
  const [getStudentStatusCertificate] = studentDocumentsApi.useLazyGetStudentStatusCertificateQuery();
  const [generateTranscript] = studentDocumentsApi.useGenerateTranscriptOfRecordsMutation();
  const [getTranscript] = studentDocumentsApi.useLazyGetTranscriptOfRecordsQuery();
  const [deleteCertificate] = studentDocumentsApi.useDeleteStudentStatusCertificateMutation();

  const { data: certificates } = studentDocumentsApi.useGetStudentStatusCertificatesQuery(
    student?.data?.studentId ?? 0,
    {
      skip: !student?.data?.studentId,
    },
  );

  const { data: transcripts } = studentDocumentsApi.useGetStudentTranscriptsQuery(student?.data?.studentId ?? 0, {
    skip: !student?.data?.studentId,
  });

  const statusTemplate = (rowData: any) => {
    return (
      <span>
        {rowData.isDraft ? <Tag value="Чернова" severity="info" /> : <Tag value="Финализиран" severity="success" />}
      </span>
    );
  };

  const handleGenerate = async (values: {
    documentNumber: string;
    documentNote: string;
    isDraft: boolean;
    documentTypeAttributeId: number | null;
    academicYearId: number | null;
    isEnrolled: boolean;
  }) => {
    try {
      if (!student?.data?.studentId) {
        return;
      }

      if (selectedDocument) {
        await updateCertificate({
          studentId: student.data.studentId,
          studentDocumentId: selectedDocument.studentDocumentId,
          ...values,
        });
      } else {
        await generateCertificate({
          studentId: student.data.studentId,
          ...values,
        });
      }

      setDialogVisible(false);
    } catch (error) {
      console.error("Error generating certificate:", error);
    }
  };

  const handleGenerateTranscript = async (values: {
    documentNumber: string;
    documentNote: string;
    isEnglish: boolean;
  }) => {
    try {
      const result = await generateTranscript({
        studentId: student?.data?.studentId!,
        ...values,
      }).unwrap();
      const blob = new Blob([result.blob], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfTitle("Академическа справка");
      setShowPdfDialog(true);
      setShowTranscriptDialog(false);
    } catch (error) {
      console.error("Error generating transcript:", error);
    }
  };

  const handleEdit = (rowData: any) => {
    setSelectedDocument(rowData);
    setDialogVisible(true);
  };

  const handleDownload = async (rowData: any) => {
    try {
      const result = await getStudentStatusCertificate(rowData.studentDocumentId).unwrap();
      const blob = new Blob([result.blob], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfTitle("Уверение");
      setShowPdfDialog(true);
    } catch (error) {
      console.error("Error downloading certificate:", error);
    }
  };

  const handleDownloadTranscript = async (_rowData: unknown) => {
    try {
      const studentId = student?.data?.studentId;
      if (!studentId) return;

      const result = await getTranscript({ studentId: studentId.toString() }).unwrap();
      const blob = new Blob([result.blob], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfTitle("Академическа справка");
      setShowPdfDialog(true);
    } catch (error) {
      console.error("Error downloading transcript:", error);
    }
  };

  const handleNewCertificate = () => {
    setSelectedDocument(null);
    setDialogVisible(true);
  };

  const handleFinalize = (rowData: any) => {
    finalizeCertificate(rowData.studentDocumentId);
  };

  const closePdfDialog = () => {
    setShowPdfDialog(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const handleDelete = (event: React.MouseEvent, rowData: any) => {
    confirmPopup({
      target: event.currentTarget as HTMLElement,
      message: "Сигурни ли сте, че искате да изтриете този документ?",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        deleteCertificate(rowData.studentDocumentId)
          .unwrap()
          .then(() => {})
          .catch((error) => {
            console.error("Error deleting document:", error);
          });
      },
    });
  };

  return (
    <div className="flex justify-content-center flex-wrap">
      <Card className="w-full mt-4">
        <div className="flex flex-row justify-content-between align-items-center">
          <h2>Уверения на студента</h2>
          <div className="flex gap-2">
            <Button
              label="Ново уверение"
              icon="pi pi-plus"
              onClick={handleNewCertificate}
              disabled={!student?.data?.studentId}
            />
          </div>
        </div>
        <Divider />
        <div className="col-12">
          <div className="card">
            <div className="p-fluid formgrid grid">
              <div className="col-12">
                <DataTable value={certificates?.data} emptyMessage="Няма намерени уверения" stripedRows>
                  <Column field="documentNumber" header="Номер на документа" sortable filter />
                  <Column field="documentNote" header="Бележка" sortable filter />
                  <Column
                    field="createdOn"
                    header="Създаден на"
                    sortable
                    filter
                    body={(rowData) => (rowData.createdOn ? format(new Date(rowData.createdOn), "dd.MM.yyyy") : "")}
                  />
                  <Column field="createdByName" header="Създаден от" sortable filter />
                  <Column field="finalizedByName" header="Финализиран от" sortable filter />
                  <Column field="isDraft" header="Статус" body={statusTemplate} sortable />
                  <Column
                    style={{ minWidth: "180px" }}
                    body={(rowData) => (
                      <>
                        {rowData.isDraft && (
                          <>
                            <Button
                              icon="pi pi-pencil"
                              rounded
                              severity="info"
                              size="small"
                              onClick={() => handleEdit(rowData)}
                              className="mr-1"
                            />
                            <Button
                              icon="pi pi-check"
                              rounded
                              severity="success"
                              size="small"
                              onClick={() => handleFinalize(rowData)}
                              className="mr-1"
                            />
                          </>
                        )}
                        <Button
                          icon="pi pi-download"
                          rounded
                          severity="success"
                          size="small"
                          onClick={() => handleDownload(rowData)}
                          className="mr-1"
                        />
                        <Button
                          icon="pi pi-trash"
                          rounded
                          severity="danger"
                          size="small"
                          onClick={(e) => handleDelete(e, rowData)}
                        />
                      </>
                    )}
                  />
                </DataTable>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card className="w-full mt-4">
        <div className="flex flex-row justify-content-between align-items-center">
          <h2>Академически справки</h2>
          <div className="flex gap-2">
            <Button label="Нова академическа справка" icon="pi pi-plus" onClick={() => setShowTranscriptDialog(true)} />
          </div>
        </div>
        <Divider />
        <div className="col-12">
          <div className="card">
            <div className="p-fluid formgrid grid">
              <div className="col-12">
                <DataTable value={transcripts?.data} emptyMessage="Няма намерени академически справки" stripedRows>
                  <Column field="documentNumber" header="Номер на документа" sortable filter />
                  <Column field="documentNote" header="Бележка" sortable filter />
                  <Column
                    field="createdOn"
                    header="Създаден на"
                    sortable
                    filter
                    body={(rowData) => format(new Date(rowData.createdOn), "dd.MM.yyyy")}
                  />
                  <Column field="createdByNavigationFullName" header="Създаден от" sortable filter />
                  <Column field="finalizedByName" header="Финализиран от" sortable filter />
                  <Column field="isDraft" header="Статус" body={statusTemplate} sortable />
                  <Column
                    style={{ minWidth: "180px" }}
                    body={(rowData) => (
                      <>
                        {rowData.isDraft && (
                          <Button
                            icon="pi pi-check"
                            rounded
                            severity="success"
                            size="small"
                            onClick={() => handleFinalize(rowData)}
                            className="mr-1"
                          />
                        )}
                        <Button
                          icon="pi pi-download"
                          rounded
                          severity="success"
                          size="small"
                          onClick={() => handleDownloadTranscript(rowData)}
                          className="mr-1"
                        />
                        <Button
                          icon="pi pi-trash"
                          rounded
                          severity="danger"
                          size="small"
                          onClick={(e) => handleDelete(e, rowData)}
                        />
                      </>
                    )}
                  />
                </DataTable>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <StudentCertificateDialog
        visible={dialogVisible}
        onHide={() => {
          setDialogVisible(false);
          setSelectedDocument(null);
        }}
        onGenerate={handleGenerate}
        document={selectedDocument}
      />

      <TranscriptOfRecordsDialog
        visible={showTranscriptDialog}
        onHide={() => setShowTranscriptDialog(false)}
        onGenerate={handleGenerateTranscript}
      />

      <FilePreviewDialog visible={showPdfDialog} onHide={closePdfDialog} fileUrl={pdfUrl} title={pdfTitle} />
      <ConfirmPopup />
    </div>
  );
};

export default StudentDocuments;
